import { AHText, Box, useScaleFactor, useTodoSorter } from "arkh";
import {
  ActivityIndicator,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PixelRatio,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useWidgetTodoSubscription } from "./useWidgetTodoSubscription";

import { graphql, usePaginationFragment } from "react-relay";
import { TodoOverviewListItemMedium } from "./TodoOverviewListItemMedium";
import { WidgetTodoOverviewContentMediumPaginated$key } from "./__generated__/WidgetTodoOverviewContentMediumPaginated.graphql";
import { useDebouncedCallback } from "use-debounce";

const { containerStyle } = StyleSheet.create({
  containerStyle: { paddingBottom: 10 },
});
export type WidgetTodoOverviewListRef = {
  scrollToItem: (itemId: string | null) => void;
};
const WidgetTodoOverviewContentMediumC = (
  props: {
    widget: WidgetTodoOverviewContentMediumPaginated$key;
    isFocused?: boolean;
    interactive?: boolean;

    shouldDimInactive: boolean;
    onPressItem: (id: string) => void;
    setScroll: (itemId: string) => void;
    highlightedItemId?: string;
    hideShowMoreButton?: boolean;
    setShouldPoll?: (hasHitTop: boolean) => void;
    enableScrollToTopTimeout?: boolean;
  },
  ref: ForwardedRef<WidgetTodoOverviewListRef>
) => {
  const {
    data: widget,
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment(
    graphql`
      fragment WidgetTodoOverviewContentMediumPaginated on WidgetTodoOverview
      @refetchable(queryName: "WidgetTodoOverviewContentMediumPaginatedQuery") {
        teams {
          id
        }
        category {
          id
        }
        todoContent: content {
          ... on WidgetTodoOverviewContentMedium {
            todos(first: $first, after: $after)
              @connection(key: "WidgetTodoOverviewContentMedium__todos") {
              __id
              edges {
                node {
                  id
                  ordinal
                  status
                  ...TodoDetailContentMain_todo
                  ...TodoOverviewListItemMedium_Todo
                }
              }
            }
          }
        }
      }
    `,
    props.widget
  );
  const todoSorter = useTodoSorter();
  const todos = useMemo(() => {
    return (widget?.todoContent?.todos?.edges ?? [])
      .map((edge) => edge.node)
      .filter((node) => node?.id)
      .sort(todoSorter);
  }, [widget, todoSorter]);
  const shared = useMemo(
    () => ({
      opacity: props.shouldDimInactive ? 0.2 : 1,
    }),
    [props.shouldDimInactive]
  );

  const renderRow = useCallback(
    ({ item }: { item: (typeof todos)[number]; index: number }) => (
      <View style={item.status !== "PENDING" ? shared : undefined}>
        <TodoOverviewListItemMedium
          isFocused={item.id === props.highlightedItemId}
          todo={item}
          onPress={props.onPressItem}
        />
      </View>
    ),
    [shared, props.highlightedItemId, props.onPressItem]
  );

  const keyExtractor = useCallback((item: (typeof todos)[0]) => item.id, []);

  const todoIds = useMemo(() => todos.map(({ id }) => id), [todos]);
  const teamIds = useMemo(
    () => widget?.teams?.map(({ id }) => id) ?? [],
    [widget.teams]
  );

  const categoryId = widget.category?.id;

  useWidgetTodoSubscription(
    teamIds,
    todoIds,
    categoryId,
    widget.todoContent?.todos?.__id ?? undefined,
    props.interactive
  );

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext && props.interactive !== false) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, props.interactive, loadNext]);
  const sf = useScaleFactor();
  const ITEM_HEIGHT = useMemo(
    () => PixelRatio.roundToNearestPixel(40 * sf),
    [sf]
  );
  const getItemLayout = useCallback(
    (_data: any, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    [ITEM_HEIGHT]
  );

  const flatListRef = useRef<FlatList>(null);
  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId) => {
      if (itemId) {
        const index = todos.findIndex((todo) => todo.id === itemId);
        if (index !== -1) {
          flatListRef.current?.scrollToIndex({ index, animated: true });
        }
      } else {
        flatListRef.current?.scrollToOffset({
          offset: 0,
          animated: true,
        });
      }
    },
  }));

  const setScroll = props.setScroll;
  const lastScrollId = useRef<string | null>(null);

  const _onScroll = useDebouncedCallback(
    useCallback(
      (y: number) => {
        const index = Math.max(0, Math.floor(y / ITEM_HEIGHT - 1));
        const item = todos[index];
        if (item && lastScrollId.current !== item.id) {
          setScroll(item.id);
          lastScrollId.current = item.id;
        }
      },
      [ITEM_HEIGHT, setScroll, todos]
    ),
    200,
    {
      leading: true,
      maxWait: 2000,
    }
  );

  const setShouldPoll = props.setShouldPoll;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const enableScrollToTopTimeout = props.enableScrollToTopTimeout;
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (props.enableScrollToTopTimeout) {
        const isAtTop = event.nativeEvent.contentOffset.y < 10;
        setShouldPoll?.(isAtTop);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          flatListRef.current?.scrollToOffset({
            offset: 0,
            animated: true,
          });
        }, 60000);
      }
      if (props.isFocused) {
        _onScroll(
          event.nativeEvent.targetContentOffset
            ? event.nativeEvent.targetContentOffset.y
            : event.nativeEvent.contentOffset.y
        );
      }
    },

    [_onScroll, setShouldPoll, enableScrollToTopTimeout]
  );
  const ListFooterComponent = useMemo(
    () => (
      <Box style={{ flex: 1 }} spaceTop="xxlarge">
        {isLoadingNext ? <ActivityIndicator /> : null}
      </Box>
    ),
    [isLoadingNext]
  );
  const onTopReached = useCallback(() => {
    if (enableScrollToTopTimeout) {
      setShouldPoll?.(true);
    }
  }, [enableScrollToTopTimeout, setShouldPoll]);

  return (
    <FlatList
      ref={flatListRef}
      initialNumToRender={15}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      ListFooterComponent={ListFooterComponent}
      keyExtractor={keyExtractor}
      data={todos}
      onEndReachedThreshold={0.5}
      onScroll={onScroll}
      renderItem={renderRow}
      onStartReached={onTopReached}
      scrollEnabled={!(props.interactive === false)}
      onEndReached={onEndReached}
      getItemLayout={getItemLayout}
      contentContainerStyle={containerStyle}
    />
  );
};

export const WidgetTodoOverviewContentMedium = React.memo(
  React.forwardRef(WidgetTodoOverviewContentMediumC)
);
